import type React from 'react'
import { useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { ObjectPanelProps } from '../types/object-panel-props'
import type { CommitBatchOrPersist, PersistShapesFromManager } from '../types/shape-commit'

/** Reports panel edits upward: per shape via `onShapeCommitted`, or as one batch when the host supports it. */
export function useShapeCommit(params: {
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onShapeCommitted: ObjectPanelProps['onShapeCommitted']
  onBatchShapesCommitted: ObjectPanelProps['onBatchShapesCommitted']
}) {
  const { shapeManagerRef, onShapeCommitted, onBatchShapesCommitted } = params

  const persistShapesFromManager: PersistShapesFromManager = useCallback(
    (objectIds, source, undo) => {
      if (!onShapeCommitted || objectIds.length === 0) return
      const mgr = shapeManagerRef?.current

      if (!mgr) return
      const all = mgr.getShapes()

      for (const id of objectIds) {
        const shape = all.find((s) => s.id === id)

        if (shape) {
          onShapeCommitted(shape, source, objectIds.length === 1 ? undo : undefined)
        }
      }
    },
    [onShapeCommitted, shapeManagerRef]
  )

  const commitBatchOrPersist: CommitBatchOrPersist = useCallback(
    (batchItems, batchSource, fallbackSource, historyTxnId) => {
      if (batchItems.length === 0) return

      if (onBatchShapesCommitted) {
        onBatchShapesCommitted(batchItems, batchSource, { historyTxnId })
      } else {
        persistShapesFromManager(
          batchItems.map((i) => i.shapeId),
          fallbackSource
        )
      }
    },
    [onBatchShapesCommitted, persistShapesFromManager]
  )

  return { persistShapesFromManager, commitBatchOrPersist }
}
